import NiceModal from '@ebay/nice-modal-react';
import { GeminiVoiceModal } from '@components/features/modals/gemini-voice/GeminiVoiceModal';
import { startResumeInterviewSession, type ResumeInterviewKind } from '@services/voice/voiceSessionApi';
import { ResumeInterviewChatModal } from './ResumeInterviewChatModal';
import { ResumeInterviewVideoModal } from './ResumeInterviewVideoModal';

/**
 * Shared launcher arguments. `getQuestions` is resolved lazily at launch time so each use case can
 * supply its own question set (and re-fetch on every open).
 */
export interface InterviewLauncherArgs {
  getQuestions: () => Promise<string[]>;
  locale: Locale;
  /** Opening line the agent speaks first (voice only) — without it Gemini Live stays silent until the user speaks. */
  firstMessage?: string;
  /** Interview variant: 'profile' (default) vs 'answer' (story-card shout-back). */
  kind?: ResumeInterviewKind;
  /** Voice only — invoked when the voice modal is closed (used to track + advance). */
  onClosed?: () => void;
  /** Video only — invoked when the video flow completes (used to track + advance). */
  onCompleted?: () => void;
}

/**
 * Voice (WebSocket) channel — opens the Gemini voice modal with a lazily-fetched backend session.
 * The signed WSS URL is short-lived, so the session is fetched just-in-time inside `fetchSession`.
 */
export const launchVoice = ({
  getQuestions, locale, firstMessage, kind = 'profile', onClosed,
}: InterviewLauncherArgs) => {
  // `autoStart` is not part of `GeminiVoiceOptions` (the modal's typed args) — the widget defaults
  // it to `true`, so the voice session auto-connects on open without passing it explicitly.
  void NiceModal.show(GeminiVoiceModal, {
    fetchSession: async () => startResumeInterviewSession(await getQuestions(), 'voice-websocket', locale, firstMessage, kind),
    onClosed,
  });
};

/** Text (web chat) channel — opens the resume-interview chat modal. */
export const launchText = ({ getQuestions, locale, kind = 'profile' }: InterviewLauncherArgs) => {
  void NiceModal.show(ResumeInterviewChatModal, { getQuestions, locale, kind });
};

/** Video channel — opens the resume-interview video modal (AiInterviewFlow). */
export const launchVideo = ({ getQuestions, locale, onCompleted }: InterviewLauncherArgs) => {
  void NiceModal.show(ResumeInterviewVideoModal, { getQuestions, locale, onCompleted });
};
